/**
 * StreamingIndicator — the "agent is working" loader.
 *
 * Shown in the message-list footer while the agent is running but hasn't yet
 * streamed text or thinking. A slowly rotating sparkle plus a shimmering
 * "Working" word and a live elapsed-time label (and token count once known),
 * so a long tool-running gap reads as active progress instead of a stalled
 * chat. Replaces the old three bouncing dots.
 *
 * While a blocking prompt is outstanding it says "Waiting for you" instead, and
 * the sparkle stops spinning (ADR-0017). The turn genuinely is still running —
 * the lease is held and the elapsed clock keeps ticking — but "Working" was an
 * outright lie in that state: the agent is idle and the only thing holding it up
 * is the user, who was being told to sit tight. This matches the sidebar, which
 * lifts the same conversation into its "Needs Input" section.
 *
 * When the agent has spawned sub-agents via the ``Task`` tool, the header also
 * shows a live count ("· 2 sub-agents") and an indented per-agent activity list
 * (type · what it's doing · elapsed · tokens) — the fix for "OmniWerk looks
 * stuck" when it's really just backgrounded sub-agents grinding away.
 */

import { useEffect, useState } from "react";

import { useChatStore, useRunningSubagents } from "@/stores/chatStore";
import { useSubagentDrillStore } from "@/stores/subagentDrillStore";
import type { SubagentInfo } from "@/api/types";
import { toolMeta } from "@/theme";
import { formatTokens } from "./breakdownShared";

/** Format elapsed ms as ``12s`` or ``1m 25s`` (matches ThinkingPanel's feel). */
function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  if (total < 60) return `${total}s`;
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}m ${s}s`;
}

/** Human-readable "what it's doing now" for a sub-agent: prefer the task
 *  description, else the friendly label of the tool it's currently running. */
function activityOf(a: SubagentInfo): string {
  if (a.description) return a.description;
  if (a.last_tool_name) return toolMeta[a.last_tool_name]?.label ?? a.last_tool_name;
  return "working";
}

/** How many sub-agent rows to render before collapsing the rest into "+N more". */
const MAX_SUBAGENT_ROWS = 4;

export function StreamingIndicator() {
  // Read this conversation's own turn anchor — per-conversation so switching
  // chats mid-turn keeps the elapsed clock instead of resetting it to 0s.
  const startedAt = useChatStore((s) =>
    s.activeConversationId ? s.turnStartedAt[s.activeConversationId] ?? null : null,
  );
  const contextTokens = useChatStore((s) => s.contextTokens);
  const subagents = useRunningSubagents();
  const drillInto = useSubagentDrillStore((s) => s.open);
  // Read the LIVE prompt state rather than the row's ``needs_input`` flag: this
  // component only ever renders for the open conversation, and the local store
  // flips the instant the widget appears, with no round-trip. The row field is
  // what the sidebar needs (it must answer for chats running elsewhere); here it
  // would only add lag.
  const blocked = useChatStore(
    (s) => !!s.pendingApproval || !!(s.pendingQuestions && s.pendingQuestions.length > 0),
  );

  // Tick every second so the elapsed labels stay live. Same cheap pattern as
  // ThinkingPanel — one interval while mounted, torn down on unmount.
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const elapsed = startedAt != null ? formatElapsed(nowTick - startedAt) : null;
  const shown = subagents.slice(0, MAX_SUBAGENT_ROWS);
  const overflow = subagents.length - shown.length;

  return (
    <div
      className="flex flex-col gap-1 py-1 select-none"
      aria-label={blocked ? "Waiting for your input" : "Agent is working"}
    >
      <div className="flex items-center gap-2.5">
        {/* Sparkle beacon — a 4-point star, slowly rotating so it reads as alive
            (bentoSpin keyframe, dialed slower than the refresh spinner). The path
            is symmetric about the viewBox centre (12,12) so it spins in place
            instead of orbiting; transform-origin is pinned to centre to match.
            Blocked: it stops and goes amber, matching the sidebar's steady dot —
            motion here would keep signalling "busy" while nothing is happening. */}
        <svg
          className={`w-4 h-4 shrink-0 ${blocked ? "text-amber-500" : "text-jove"}`}
          style={
            blocked
              ? undefined
              : { animation: "bentoSpin 2s linear infinite", transformOrigin: "center" }
          }
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M12 3c.9 5.4 3.7 8.1 9 9-5.3.9-8.1 3.6-9 9-.9-5.4-3.7-8.1-9-9 5.3-.9 8.1-3.6 9-9z" />
        </svg>
        <span className="text-sm text-space-50">
          {blocked ? (
            // No ``working-text`` shimmer: that animation is the "busy" tell.
            <span className="font-medium text-amber-600">Waiting for you</span>
          ) : (
            <span className="working-text font-medium">Working</span>
          )}
          {subagents.length > 0 ? (
            <span className="text-space-40">
              {" "}· {subagents.length} sub-agent{subagents.length === 1 ? "" : "s"}
            </span>
          ) : null}
          {elapsed ? <span className="text-space-40"> · {elapsed}</span> : null}
          {contextTokens > 0 ? (
            <span className="text-space-40"> · {formatTokens(contextTokens)} tokens</span>
          ) : null}
        </span>
      </div>

      {/* Per-sub-agent activity — the "it's not stuck, this agent is busy" cue. */}
      {shown.length > 0 ? (
        <div className="flex flex-col gap-0.5 pl-[26px] text-xs text-space-40">
          {shown.map((a) => (
            // Clicking a row opens the drill-in modal on that sub-agent's own
            // stream — the "what is it actually doing?" answer.
            <button
              key={a.tool_use_id}
              type="button"
              onClick={() => drillInto(a.tool_use_id)}
              title="View this sub-agent's activity"
              className="flex items-center gap-1.5 min-w-0 text-left rounded
                         hover:bg-space-20/60 hover:text-space-50 transition-colors
                         px-1 -mx-1 py-0.5"
            >
              <span className="w-1 h-1 rounded-full bg-jove/70 shrink-0" aria-hidden="true" />
              {a.agent_type ? (
                <span className="text-space-50 font-medium shrink-0">{a.agent_type}</span>
              ) : null}
              <span className="truncate">{activityOf(a)}</span>
              <span className="shrink-0"> · {formatElapsed(nowTick - a.started_at)}</span>
              {a.total_tokens > 0 ? (
                <span className="shrink-0"> · {formatTokens(a.total_tokens)} tok</span>
              ) : null}
            </button>
          ))}
          {overflow > 0 ? <div className="pl-[10px]">+{overflow} more</div> : null}
        </div>
      ) : null}
    </div>
  );
}
